import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { LEARNING_CONTENT_QUERY } from '../graphql';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';

const CATEGORIES = ['All', 'Legal & Rights', 'Music', 'Art & Design', 'Programming', 'Sports', 'Cooking', 'Photography', 'Writing', 'Science', 'General'];
const TYPE_ICONS = { ARTICLE: '📄', VIDEO: '🎬', AUDIO: '🎵' };
const TYPE_LABELS = { ARTICLE: 'Article', VIDEO: 'Video', AUDIO: 'Audio' };

const SUBMIT_CATEGORIES = ['Programming', 'Music', 'Art', 'Cooking', 'Sports', 'Science', 'Other'];
const STORAGE_KEY = 'agora_learn_resources';

function extractYouTubeId(url) {
  try {
    if (url.includes('youtu.be/')) {
      const parts = url.split('youtu.be/');
      return parts[1]?.split(/[?&#]/)[0] || null;
    }
    const u = new URL(url);
    return u.searchParams.get('v') || null;
  } catch {
    return null;
  }
}

function isValidYouTubeUrl(url) {
  return url.startsWith('https://www.youtube.com/') || url.startsWith('https://youtu.be/');
}

function loadResources() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveResources(resources) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(resources));
}

function YouTubeCard({ resource, currentUser, onDelete }) {
  const isAdmin = currentUser?.role === 'ADMIN';
  const isOwner = currentUser?.id === resource.submittedBy;
  const canDelete = isAdmin || isOwner;

  return (
    <div className="card" style={{ position: 'relative', transition: 'all 0.15s' }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none'; }}>
      {canDelete && (
        <button
          onClick={() => onDelete(resource.id)}
          title="Delete resource"
          style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(220,38,38,0.1)', border: 'none', borderRadius: 6, cursor: 'pointer', color: '#dc2626', fontSize: 13, padding: '3px 7px', zIndex: 1 }}>
          ✕
        </button>
      )}
      <a href={resource.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
        <div style={{ borderRadius: 'var(--radius-sm)', overflow: 'hidden', marginBottom: 12, height: 160, background: 'var(--surface2)', position: 'relative' }}>
          <img
            src={`https://img.youtube.com/vi/${resource.videoId}/mqdefault.jpg`}
            alt={resource.title}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: 'rgba(0,0,0,0.65)', borderRadius: '50%', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>▶</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          <span className="badge" style={{ background: 'var(--primary-light)', color: 'var(--primary)', fontSize: 11 }}>🎬 Video</span>
          <span className="badge badge-dim" style={{ fontSize: 11 }}>{resource.category}</span>
          <span className="badge badge-dim" style={{ fontSize: 11 }}>YouTube</span>
        </div>
        <h3 style={{ fontWeight: 700, fontSize: 'var(--font-base)', marginBottom: 6, lineHeight: 1.4, color: 'var(--text)' }}>{resource.title}</h3>
        {resource.description && (
          <p style={{ fontSize: 'var(--font-sm)', color: 'var(--text-muted)', lineHeight: 1.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {resource.description}
          </p>
        )}
      </a>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>By community member</span>
        <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{new Date(resource.submittedAt).toLocaleDateString()}</span>
      </div>
    </div>
  );
}

function AddResourceForm({ currentUser, onAdd, onCancel }) {
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('Other');
  const [urlError, setUrlError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  function handleUrlChange(e) {
    const val = e.target.value;
    setUrl(val);
    if (val && !isValidYouTubeUrl(val)) {
      setUrlError('URL must start with https://www.youtube.com/ or https://youtu.be/');
    } else {
      setUrlError('');
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!url || !title) return;
    if (!isValidYouTubeUrl(url)) {
      setUrlError('URL must start with https://www.youtube.com/ or https://youtu.be/');
      return;
    }
    const videoId = extractYouTubeId(url);
    if (!videoId) {
      setUrlError('Could not extract a YouTube video ID from this URL.');
      return;
    }
    setSubmitting(true);
    const newResource = {
      id: Date.now().toString(),
      title: title.trim(),
      description: description.trim(),
      url,
      videoId,
      category,
      submittedBy: currentUser?.id || 'anonymous',
      submittedAt: new Date().toISOString(),
    };
    const existing = loadResources();
    saveResources([newResource, ...existing]);
    onAdd(newResource);
    setSubmitting(false);
  }

  return (
    <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20, marginBottom: 24 }}>
      <h3 style={{ fontWeight: 700, fontSize: 'var(--font-base)', marginBottom: 16 }}>Add a YouTube Resource</h3>
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gap: 12 }}>
          <div>
            <label style={{ fontSize: 'var(--font-sm)', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>YouTube URL *</label>
            <input
              className="input"
              style={{ width: '100%' }}
              placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..."
              value={url}
              onChange={handleUrlChange}
              required
            />
            {urlError && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{urlError}</p>}
          </div>
          <div>
            <label style={{ fontSize: 'var(--font-sm)', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Title *</label>
            <input
              className="input"
              style={{ width: '100%' }}
              placeholder="Resource title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              required
            />
          </div>
          <div>
            <label style={{ fontSize: 'var(--font-sm)', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Description (optional)</label>
            <input
              className="input"
              style={{ width: '100%' }}
              placeholder="Brief description of this resource"
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
          </div>
          <div>
            <label style={{ fontSize: 'var(--font-sm)', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Category</label>
            <select className="input" value={category} onChange={e => setCategory(e.target.value)} style={{ minWidth: 160 }}>
              {SUBMIT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <button type="button" onClick={onCancel} style={{ padding: '8px 18px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: 'var(--font-sm)', color: 'var(--text-muted)' }}>Cancel</button>
            <button type="submit" disabled={submitting || !url || !title || !!urlError} className="btn-primary" style={{ padding: '8px 20px', fontSize: 'var(--font-sm)', opacity: (!url || !title || !!urlError) ? 0.5 : 1 }}>
              Add Resource
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function ContentCard({ item, onClick }) {
  return (
    <div className="card" style={{ cursor: 'pointer', transition: 'all 0.15s' }}
      onClick={() => onClick(item)}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none'; }}>
      {item.thumbnailUrl && (
        <div style={{ borderRadius: 'var(--radius-sm)', overflow: 'hidden', marginBottom: 12, height: 160, background: 'var(--surface2)' }}>
          <img src={item.thumbnailUrl} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
      )}
      {!item.thumbnailUrl && item.contentType === 'VIDEO' && (
        <div style={{ borderRadius: 'var(--radius-sm)', overflow: 'hidden', marginBottom: 12, height: 120, background: 'linear-gradient(135deg,#6366f120,#8b5cf620)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 48 }}>
          🎬
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <span className="badge" style={{ background: 'var(--primary-light)', color: 'var(--primary)', fontSize: 11 }}>
          {TYPE_ICONS[item.contentType]} {TYPE_LABELS[item.contentType]}
        </span>
        <span className="badge badge-dim" style={{ fontSize: 11 }}>{item.category}</span>
      </div>
      <h3 style={{ fontWeight: 700, fontSize: 'var(--font-base)', marginBottom: 6, lineHeight: 1.4 }}>{item.title}</h3>
      {item.body && (
        <p style={{ fontSize: 'var(--font-sm)', color: 'var(--text-muted)', lineHeight: 1.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {item.body.replace(/<[^>]+>/g, '')}
        </p>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div className="avatar avatar-xs" style={{ background: item.author?.avatarColor || '#6366f1', width: 22, height: 22, fontSize: 11 }}>{item.author?.name?.[0] || 'A'}</div>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{item.author?.name || 'Admin'}</span>
        </div>
        <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>👁 {item.viewCount}</span>
      </div>
    </div>
  );
}

function ContentModal({ item, onClose }) {
  if (!item) return null;
  const isYT = item.mediaUrl?.match(/youtube\.com|youtu\.be/);
  const ytId = isYT ? item.mediaUrl.match(/(?:v=|youtu\.be\/)([^&\s]+)/)?.[1] : null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: 16 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', maxWidth: 720, width: '100%', maxHeight: '90vh', overflow: 'auto', padding: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <span className="badge" style={{ background: 'var(--primary-light)', color: 'var(--primary)' }}>{TYPE_ICONS[item.contentType]} {TYPE_LABELS[item.contentType]}</span>
              <span className="badge badge-dim">{item.category}</span>
            </div>
            <h2 style={{ fontWeight: 800, fontSize: 'var(--font-xl)', lineHeight: 1.3 }}>{item.title}</h2>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>✕</button>
        </div>

        {/* Media player */}
        {item.contentType === 'VIDEO' && item.mediaUrl && (
          <div style={{ marginBottom: 20, borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
            {ytId ? (
              <iframe width="100%" height="380" src={`https://www.youtube.com/embed/${ytId}`} frameBorder="0" allowFullScreen title={item.title} />
            ) : (
              <video controls style={{ width: '100%', borderRadius: 'var(--radius-sm)' }} src={item.mediaUrl} />
            )}
          </div>
        )}
        {item.contentType === 'AUDIO' && item.mediaUrl && (
          <div style={{ marginBottom: 20, padding: '16px', background: 'var(--surface2)', borderRadius: 'var(--radius-sm)' }}>
            <div style={{ fontSize: 32, textAlign: 'center', marginBottom: 8 }}>🎵</div>
            <audio controls style={{ width: '100%' }} src={item.mediaUrl} />
          </div>
        )}

        {/* Body */}
        {item.body && (
          <div style={{ fontSize: 'var(--font-base)', lineHeight: 1.75, color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{item.body}</div>
        )}

        {/* Tags */}
        {item.tags?.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 20 }}>
            {item.tags.map(t => <span key={t} className="badge badge-dim">#{t}</span>)}
          </div>
        )}

        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="avatar avatar-sm" style={{ background: item.author?.avatarColor }}>{item.author?.name?.[0]}</div>
            <span style={{ fontSize: 'var(--font-sm)', color: 'var(--text-muted)' }}>{item.author?.name}</span>
          </div>
          <span style={{ fontSize: 'var(--font-sm)', color: 'var(--text-dim)' }}>👁 {item.viewCount} views</span>
        </div>
      </div>
    </div>
  );
}

export default function Learn() {
  const [category, setCategory] = useState('');
  const [type, setType] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [communityResources, setCommunityResources] = useState([]);

  const { user: currentUser } = useAuth();

  useEffect(() => {
    setCommunityResources(loadResources());
  }, []);

  const { data, loading } = useQuery(LEARNING_CONTENT_QUERY, {
    variables: { category: category || null, contentType: type || null, search: search || null },
  });

  const items = data?.learningContent || [];

  function handleResourceAdded(resource) {
    setCommunityResources(prev => [resource, ...prev]);
    setShowAddForm(false);
  }

  function handleDeleteResource(id) {
    const updated = communityResources.filter(r => r.id !== id);
    saveResources(updated);
    setCommunityResources(updated);
  }

  return (
    <div className="page">
      <div className="container" style={{ padding: '32px 16px', maxWidth: 1000 }}>
        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 'var(--font-xl)', fontWeight: 800, marginBottom: 6 }}>📚 Agora Learning Library</h1>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)' }}>Videos, articles, and audio lessons curated by the Agora community</p>
          </div>
          {currentUser && !showAddForm && (
            <button
              onClick={() => setShowAddForm(true)}
              className="btn-primary"
              style={{ padding: '8px 18px', fontSize: 'var(--font-sm)', whiteSpace: 'nowrap' }}>
              + Add Resource
            </button>
          )}
        </div>

        {/* Add resource form */}
        {showAddForm && currentUser && (
          <AddResourceForm
            currentUser={currentUser}
            onAdd={handleResourceAdded}
            onCancel={() => setShowAddForm(false)}
          />
        )}

        {/* Community-submitted YouTube resources */}
        {communityResources.length > 0 && (
          <div style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: 'var(--font-lg)', fontWeight: 700, marginBottom: 14 }}>Community Resources</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 16 }}>
              {communityResources.map(resource => (
                <YouTubeCard
                  key={resource.id}
                  resource={resource}
                  currentUser={currentUser}
                  onDelete={handleDeleteResource}
                />
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 24 }}>
          <input className="input" placeholder="🔍 Search content…" value={search} onChange={e => setSearch(e.target.value)} style={{ minWidth: 220 }} />
          <select className="input" value={type} onChange={e => setType(e.target.value)} style={{ minWidth: 130 }}>
            <option value="">All types</option>
            <option value="VIDEO">🎬 Video</option>
            <option value="ARTICLE">📄 Article</option>
            <option value="AUDIO">🎵 Audio</option>
          </select>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {CATEGORIES.map(c => (
              <button key={c} onClick={() => setCategory(c === 'All' ? '' : c)}
                style={{ padding: '6px 14px', borderRadius: 20, border: `2px solid ${(category || 'All') === c ? 'var(--primary)' : 'var(--border)'}`, background: (category || 'All') === c ? 'var(--primary-light)' : 'transparent', color: (category || 'All') === c ? 'var(--primary)' : 'var(--text-muted)', fontWeight: 600, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>
                {c}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : items.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📚</div>
            <div className="empty-state-title">No content yet</div>
            <div className="empty-state-desc">Admins can add learning materials from the Admin Dashboard.</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 16 }}>
            {items.map(item => <ContentCard key={item.id} item={item} onClick={setSelected} />)}
          </div>
        )}
      </div>

      {selected && <ContentModal item={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
